import {
  Button,
  InlineNotification,
  Loading,
  TextArea,
  Layer,
} from '@carbon/react';
import { EmptyState } from 'components/empty-state';
import { Events as EventsIcon } from 'components/icons';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { VerificationItemEventResponse } from 'types/api-responses';
import { formatResource } from 'utils/misc';
import {
  createVerificationItemEventsAtom,
  verificationItemCommentMutationAtom,
} from './atoms';
import { VerificationEvent } from './verification-event';
import style from './verification-events-list.module.scss';

interface VerificationEventsListProps {
  title: string;
  helperText?: string;
  eventsHref: string;
  commentsHref: string;
  onComment?: () => void;
  onEventsLoaded?: (commentsCount: number) => void;
}

const VerificationEventsList = ({
  title,
  helperText,
  eventsHref,
  commentsHref,
  onComment,
  onEventsLoaded,
}: VerificationEventsListProps) => {
  const eventsAtom = useMemo(
    () => createVerificationItemEventsAtom(eventsHref),
    [eventsHref]
  );
  const eventsResource = useAtomValue(eventsAtom);
  const fetchEvents = useSetAtom(eventsAtom);
  const [commentMutationState] = useAtom(verificationItemCommentMutationAtom);

  const { data: eventsData, loading: eventsLoading } =
    formatResource(eventsResource);
  const events = (eventsData as VerificationItemEventResponse[]) || [];
  const loading = eventsLoading || commentMutationState.loading;

  useEffect(() => {
    if (eventsData && Array.isArray(eventsData) && !loading && onEventsLoaded) {
      const commentsCount = eventsData.filter(
        (e) => e.kind === 'create_comment'
      ).length;
      onEventsLoaded(commentsCount);
    }
  }, [eventsData, loading, onEventsLoaded]);

  if (loading) return <Loading />;
  if (eventsData && 'error' in eventsData)
    return (
      <InlineNotification
        title={eventsData.error as string}
        kind="error"
        lowContrast
      />
    );

  return (
    <div className={style.panel}>
      <div className={style.content}>
        <div className={style.events}>
          {events.length > 0 ? (
            <ul>
              {events
                .sort(
                  (a, b) =>
                    new Date(a.createdAt).getTime() -
                    new Date(b.createdAt).getTime()
                )
                .map((item) => (
                  <VerificationEvent key={item.id} item={item} />
                ))}
            </ul>
          ) : (
            <EmptyState
              IconComponent={EventsIcon}
              headerText={title}
              helperText={helperText}
              className={style.empty}
            />
          )}
        </div>
        <Input
          getEvents={fetchEvents}
          commentsHref={commentsHref}
          onComment={onComment}
          loading={commentMutationState.loading}
        />
      </div>
    </div>
  );
};

const Input = ({
  getEvents,
  commentsHref,
  onComment,
  loading,
}: {
  getEvents?: () => Promise<void>;
  commentsHref: string;
  onComment?: () => void;
  loading: boolean;
}) => {
  const [comment, setComment] = useState('');
  const commentMutation = useSetAtom(verificationItemCommentMutationAtom);
  return (
    <div className={style.input}>
      <Layer>
        <TextArea
          labelText=""
          placeholder="Comment"
          autoFocus={true}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </Layer>
      <div className={style.buttons}>
        <Button
          type="submit"
          disabled={!comment || loading}
          onClick={() => {
            commentMutation({
              url: commentsHref,
              type: 'POST',
              data: { content: comment },
              onSuccess: () => {
                onComment?.();
                setComment('');
                getEvents?.();
              },
            });
          }}
        >
          Post
        </Button>
      </div>
    </div>
  );
};

export { VerificationEventsList };
